import { useObservable } from '@legendapp/state/react';
import { useEffect, useRef } from 'react';
import { databaseService } from '@/services/databaseService';
import APP_CONSTANTS from '@/config/constants';
import { realtimeTrigger } from '@/state/realtime';
import { globalState } from '@/state/globalState';
import { AdminLogger } from '@/utils/logger';
import { isMotoristaUserType } from '@/utils/userRoles';

// --- Caches globais (memória da sessão) ---
const templateCache: Record<string, string> = {};
const userCache: Record<string, string> = {};

function clearDashboardMetadataCaches() {
  Object.keys(templateCache).forEach((key) => {
    delete templateCache[key];
  });
  Object.keys(userCache).forEach((key) => {
    delete userCache[key];
  });
}

export interface DashboardFilters {
  startDate: number | null;
  endDate: number | null;
  status: 'in_progress' | 'done' | null;
  templateId: string | null;
  page: number; // paginação (0-based)
}

const PAGE_SIZE = 50;

// Logger dedicado ao dashboard admin
const log = AdminLogger.Dashboard;

export function useDashboardData(filters: DashboardFilters) {
  const state = useObservable({
    kpis: {
      done: 0,
      inProgress: 0,
      averageTime: 0,
    },
    list: [] as any[],
    totalCount: 0,
    hasMore: false,
    loading: true,
  });

  const profileSnapshot = globalState.user.profile.get();
  const empresaId = profileSnapshot?.empresa_id || null;
  const currentUserId = globalState.user.user.get()?.id || null;
  const motoristaScopeUserId = isMotoristaUserType(profileSnapshot?.user_type) ? currentUserId : null;
  const realtimeCounter = realtimeTrigger.counter.get();

  // Helper para decidir quando redefinir a lista ou concatenar (scroll infinito)
  const shouldAppend = (prevFilters: DashboardFilters, newFilters: DashboardFilters) => {
    // Se apenas a página avançou, mantemos concatenação; qualquer outro filtro reinicia
    return (
      prevFilters.startDate === newFilters.startDate &&
      prevFilters.endDate === newFilters.endDate &&
      prevFilters.status === newFilters.status &&
      prevFilters.templateId === newFilters.templateId &&
      newFilters.page > prevFilters.page
    );
  };

  // Guardar filtros anteriores para lógica de concatenação
  const previousFiltersRef = useRef<DashboardFilters | null>(null);
  const lastRealtimeCounterRef = useRef<number | null>(null);

  useEffect(() => {
    if (lastRealtimeCounterRef.current !== null && lastRealtimeCounterRef.current !== realtimeCounter) {
      clearDashboardMetadataCaches();
      log.info('Cache de metadados do dashboard invalidado após atualização realtime/sync');
    }
    lastRealtimeCounterRef.current = realtimeCounter;

    const currentFilters = filters;
    log.info('🔄 Iniciando carregamento do Dashboard', currentFilters);

    async function load() {
      if (!empresaId) {
        log.info('EmpresaId não disponível no globalState; abortando load');
        state.loading.set(false);
        state.list.set([]);
        return;
      }

      if (isMotoristaUserType(profileSnapshot?.user_type) && !motoristaScopeUserId) {
        log.warn('Motorista sem userId disponível; retornando dashboard vazio por segurança');
        state.kpis.done.set(0);
        state.kpis.inProgress.set(0);
        state.kpis.averageTime.set(0);
        state.list.set([]);
        state.totalCount.set(0);
        state.hasMore.set(false);
        state.loading.set(false);
        return;
      }

      state.loading.set(true);

      const t0 = Date.now();
      // Build basic query
      const where: any = {
        empresa_id: empresaId,
      };
      if (motoristaScopeUserId) where.usuario_id = motoristaScopeUserId;
      if (filters.status) where.status = filters.status;
      if (filters.templateId) where.template_id = filters.templateId;
      if (filters.startDate) where.data_criacao = { $gte: filters.startDate };
      if (filters.endDate) {
        where.data_criacao = {
          ...(where.data_criacao || {}),
          $lte: filters.endDate,
        };
      }

      // KPIs
      const allItems = (await databaseService.listItems<any>(
        APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME,
        where
      )).filter(item => !item.retired_at);
      const totalCount = allItems.length;
      const inProgress = allItems.filter((c) => c.status === 'in_progress');
      const doneItems = allItems.filter((c) => c.status === 'done');
      const averageTimes = allItems
        .filter((c) => c.status === 'done' && c.data_conclusao)
        .map((c) => {
          const start = typeof c.data_criacao === 'number' ? c.data_criacao : new Date(c.data_criacao).getTime();
          const end = typeof c.data_conclusao === 'number' ? c.data_conclusao : new Date(c.data_conclusao).getTime();
          return Number.isFinite(start) && Number.isFinite(end) ? Math.max(0, end - start) : 0;
        })
        .filter((delta) => Number.isFinite(delta) && delta > 0);
      const averageTime = averageTimes.length > 0
        ? Math.round(averageTimes.reduce((a, b) => a + b, 0) / averageTimes.length)
        : 0;
      log.info('KPI tempo médio calculado', {
        totalConcluidos: doneItems.length,
        amostrasTempo: averageTimes.length,
        mediaMs: averageTime,
        mediaMinutos: Number((averageTime / 60000).toFixed(2)),
      });

      log.info(`Obtidos ${totalCount} checklists para cálculo de KPIs`);

      // Atualização granular garante notificação de observables profundos
      state.kpis.done.set(doneItems.length);
      state.kpis.inProgress.set(inProgress.length);
      state.kpis.averageTime.set(averageTime);
      state.totalCount.set(totalCount);

      // NEW LOG: tempo até KPIs estarem prontos
      const kpiElapsed = Date.now() - t0;
      log.info(`⏱️ Tempo parcial – KPIs prontos em ${kpiElapsed}ms`);

      log.info('KPIs atualizados', {
        done: doneItems.length,
        inProgress: inProgress.length,
        averageTimeMs: averageTime,
      });

      // Lista paginada a partir da coleção ativa já filtrada para não criar
      // buracos quando registros aposentados ocupam posições anteriores.
      const pageStart = filters.page * PAGE_SIZE;
      const paginatedRaw = allItems.slice(pageStart, pageStart + PAGE_SIZE);

      // determinar se há mais a carregar
      const hasMore = totalCount > (filters.page + 1) * PAGE_SIZE;
      state.hasMore.set(hasMore);

      // Enriquecer lista com nomes de template e usuário
      const templateIds = Array.from(new Set(paginatedRaw.map((p) => p.template_id)));
      const userIds = Array.from(new Set(paginatedRaw.map((p) => p.usuario_id).filter(Boolean)));

      // Verifica itens que ainda não estão em cache
      const missingTemplateIds = templateIds.filter((id) => !templateCache[id]);
      const missingUserIds = userIds.filter((id) => !userCache[id]);

      // Busca em paralelo apenas o que falta
      await Promise.all([
        Promise.all(
          missingTemplateIds.map(async (tid) => {
            const t = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME, tid);
            if (t) templateCache[tid] = t.nome;
          }),
        ),
        Promise.all(
          missingUserIds.map(async (uid) => {
            const u = await databaseService.readItem<any>(APP_CONSTANTS.DATABASE.TABLES.USERS.NAME, uid);
            if (u) userCache[uid] = u.name || u.email || '–';
          }),
        ),
      ]);

      const templateNameMap: Record<string, string> = { ...templateCache };
      const userNameMap: Record<string, string> = { ...userCache };

      const paginated = paginatedRaw.map((item) => ({
        ...item,
        templateName: templateNameMap[item.template_id] || '',
        userName: userNameMap[item.usuario_id] || '',
      }));

      if (previousFiltersRef.current && shouldAppend(previousFiltersRef.current, currentFilters)) {
        // Scroll incremental: concatenar mantendo itens únicos pelo id
        const combined = [
          ...state.list.peek(),
          ...paginated.filter((item) => !state.list.peek().some((e: any) => e.id === item.id)),
        ];
        state.list.set(combined);
        log.info(`Concatenei ${paginated.length} itens (scroll). Total agora: ${combined.length}`);
      } else {
        // Novo conjunto de filtros: reiniciar
        state.list.set(paginated);
        log.info(`Lista reiniciada com ${paginated.length} itens`);
      }

      // NEW LOG: tempo até lista estar pronta (antes de marcar loading=false)
      const listElapsed = Date.now() - t0;
      log.info(`⏱️ Tempo parcial – lista preparada em ${listElapsed}ms`);

      previousFiltersRef.current = currentFilters;
      state.loading.set(false);

      const elapsed = Date.now() - t0;
      log.info(`✅ Carregamento finalizado em ${elapsed}ms`);
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), realtimeCounter, empresaId, motoristaScopeUserId, profileSnapshot?.user_type]);

  return state;
} 
